/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import { Button, Checkbox, DialogContent, DialogContentText, FormControlLabel, IconButton, Typography } from '@mui/material';
import {
  ArrowRight, Casino, Check, Close, Favorite, FavoriteBorderOutlined,
  HowToVote, OpenInBrowser, PlayArrow, Print, QuestionAnswer, Report, Wash
} from '@mui/icons-material';
import { TaskBasketItem, PenaltyBasketItem, BaseTimedBasketItem, BaseBasketItem } from '../ts/Definitions';
import ImageDisplayField from '../fields/ImageDisplayField';
import { examineTask, ITaskFlags } from '../utilities/taskUtils';
import { SelectedSettingsContext } from '../ts/Contexts';
import { green, red } from '@mui/material/colors';
import ActivityRings from 'react-activity-rings'
import { CountType, ItemTypes, TaskType } from '../ts/enums';
import { Breakpoint } from 'react-socks';
import ScheduleItemCard, { ScheduleItemProps } from '../itemViewers/ScheduleItem';
import Webcam from 'react-webcam';
import HelpIcon from '../controls/HelpIcon';
import { getRandom } from '../utilities/utils';
import { printScheduleEvent } from '../utilities/schedulePrintUtils';
import attendanceBellAudio from '../assets/attendanceBell.mp3';

interface Props extends ScheduleItemProps {
  callback: () => void;
  /** Called immediately after the Spin action fires (e.g. game contexts that need to advance on spin). */
  onSpin?: () => void;
}

//include webcam hook for positions
export default function ScheduleItemDialog(props: Props): JSX.Element {
  type CompletionAlertMode = 'default' | '30s' | 'continuous';
  const COMPLETION_ALERT_MODE_KEY = 'monitoredCompletionAlertMode';
  const webcamRef = useRef<Webcam | null>(null); // create a webcam reference
  const settings = useContext(SelectedSettingsContext);
  const [imgSnaps, setImgSnaps] = useState<string[]>([]); // initialize it
  const snapsRef = useRef(imgSnaps);
  const captureTimerHandle = useRef<ReturnType<typeof setTimeout>>();
  const stateRefreshHandle = useRef<ReturnType<typeof setInterval>>();
  const [downloadFilesOnComplete, setDownloadFilesOnComplete] = useState<boolean>(false);
  const downloadFilesOnCompleteRef = useRef(downloadFilesOnComplete);
  const [completionAlertMode, setCompletionAlertMode] = useState<CompletionAlertMode>(() => {
    const stored = localStorage.getItem(COMPLETION_ALERT_MODE_KEY);
    if (stored === '30s' || stored === 'continuous' || stored === 'default') return stored;
    return 'default';
  });
  const completionAlertAudioRef = useRef<HTMLAudioElement | null>(null);
  const completionAlertTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completionAlertPlayedRef = useRef(false);
  const completionRunStartedRef = useRef(false);
  const [settingProps, setSettingProps] = useState<ITaskFlags>(examineTask(settings, props));
  const [linkedChildTasks, setLinkedChildTasks] = useState<BaseBasketItem[]>([]);
  const linkedChildSignatureRef = useRef('');
  // Always-current ref so stale timer callbacks (capture) can read fresh values
  const liveRef = useRef({ settings, props });
  liveRef.current = { settings, props };
  const { item, readonly,
    checkEventComplete,
    callback } = props;

  const getLiveProps = useCallback((): ScheduleItemProps => {
    const currentProps = liveRef.current.props;
    const currentItem = currentProps.item as BaseBasketItem;

    if (currentItem.itemType === ItemTypes.task || currentItem.itemType === ItemTypes.penalty) {
      const scheduled = liveRef.current.settings.getTaskSchedule()[currentItem.key];
      if (scheduled) {
        const hydratedItem = currentItem.itemType === ItemTypes.task
          ? new TaskBasketItem(scheduled as any)
          : new PenaltyBasketItem(scheduled as any);
        return { ...currentProps, item: hydratedItem as any };
      }
    }

    return currentProps;
  }, []);

  const showScheduleDialogNoop = useCallback(() => {
    // Embedded linked tasks are informational only in this dialog.
  }, []);

  const refreshLinkedChildTasks = useCallback(() => {
    const currentItem = liveRef.current.props.item as TaskBasketItem & { customScheduleKey?: string };
    const isParentCustomEventTask = currentItem.itemType === ItemTypes.task
      && currentItem.creditTaskType === TaskType.acknowledgement;
    if (!isParentCustomEventTask) {
      if (linkedChildSignatureRef.current !== '') {
        linkedChildSignatureRef.current = '';
        setLinkedChildTasks([]);
      }
      return;
    }

    const basket = liveRef.current.settings.getBasketItems(false);
    const allItems = [...basket.pendingItems, ...basket.tasks, ...basket.penalties] as BaseBasketItem[];
    const linked = allItems.filter((basketItem) => {
      if (!basketItem || basketItem.key === currentItem.key) return false;
      const candidate = basketItem as BaseBasketItem & {
        requestedByKey?: string;
        customScheduleKey?: string;
      };
      const sameRequestedBy = candidate.requestedByKey === currentItem.key;
      const sameCustomSchedule = !!candidate.customScheduleKey
        && !!currentItem.customScheduleKey
        && candidate.customScheduleKey === currentItem.customScheduleKey;
      return sameRequestedBy || sameCustomSchedule;
    });

    linked.sort((left, right) => {
      if (left.isComplete !== right.isComplete) return left.isComplete ? 1 : -1;
      if (left.isPaused !== right.isPaused) return left.isPaused ? -1 : 1;
      return left.name.localeCompare(right.name);
    });

    const signature = linked.map((linkedItem) => {
      const timed = linkedItem as BaseTimedBasketItem;
      return `${linkedItem.key}|${linkedItem.isPaused ? 1 : 0}|${linkedItem.isComplete ? 1 : 0}|${timed.timerTicks ?? ''}|${timed.expectedCount ?? ''}`;
    }).join(';');

    if (signature === linkedChildSignatureRef.current) return;
    linkedChildSignatureRef.current = signature;
    setLinkedChildTasks(linked);
  }, []);

  const handleClose = useCallback((): void => {
    if (completionAlertTimeoutRef.current) {
      clearTimeout(completionAlertTimeoutRef.current);
      completionAlertTimeoutRef.current = null;
    }
    if (completionAlertAudioRef.current) {
      completionAlertAudioRef.current.pause();
      completionAlertAudioRef.current.currentTime = 0;
      completionAlertAudioRef.current = null;
    }
    callback();
  }, [callback]);

  const closeAfterPersist = useCallback(() => {
    // Ensure debounced state updates are pushed before closing the dialog.
    void settings.flushPendingStateUpdates().finally(() => {
      handleClose();
    });
  }, [handleClose, settings]);

  const handleCloseClick = useCallback((_args: any, reason?: string) => {
    if (settingProps.canCloseWindow && reason != 'backdropClick') {
      handleClose();
      return;
    }
  }, [handleClose, settingProps]);

  const refreshTaskFlags = useCallback(() => {
    const liveProps = getLiveProps();
    const nextProps = examineTask(liveRef.current.settings, liveProps);
    setSettingProps(prev => {
      // Bail out if the values that actually drive the render haven't changed —
      // avoids a full Dialog re-render on every 1-second tick when nothing has moved.
      if (
        prev.percentRemaining === nextProps.percentRemaining &&
        prev.inProgress === nextProps.inProgress &&
        prev.showCompletedIcon === nextProps.showCompletedIcon &&
        prev.canCloseWindow === nextProps.canCloseWindow &&
        prev.showTimerIcon === nextProps.showTimerIcon &&
        prev.showSpin === nextProps.showSpin &&
        prev.showStartButton === nextProps.showStartButton &&
        prev.showBeginTimerButton === nextProps.showBeginTimerButton &&
        prev.showDoneButton === nextProps.showDoneButton &&
        prev.showOkButton === nextProps.showOkButton &&
        prev.showDecrementCountButton === nextProps.showDecrementCountButton
      ) {
        return prev;
      }
      return nextProps;
    });
    if (liveRef.current.props.item.itemType === ItemTypes.position
      && !(liveRef.current.props.item as TaskBasketItem & { hasMetronome?: boolean }).hasMetronome
      && nextProps.showCompletedIcon
      && captureTimerHandle.current) {
      clearTimeout(captureTimerHandle.current);
      captureTimerHandle.current = undefined;
    }
    refreshLinkedChildTasks();
  }, [getLiveProps, refreshLinkedChildTasks]);

  useEffect(() => {
    refreshTaskFlags();
  }, [item.itemType, props, settings, refreshTaskFlags]);

  useEffect(() => {
    if (!settingProps.timed && item.itemType !== ItemTypes.position) return;
    if (stateRefreshHandle.current) clearInterval(stateRefreshHandle.current);
    stateRefreshHandle.current = setInterval(() => {
      refreshTaskFlags();
    }, 1000);

    return () => {
      if (stateRefreshHandle.current) {
        clearInterval(stateRefreshHandle.current);
        stateRefreshHandle.current = undefined;
      }
    };
  }, [item.itemType, refreshTaskFlags, settingProps.timed]);

  const capture = useCallback(() => {
    clearTimeout(captureTimerHandle.current);
    captureTimerHandle.current = setTimeout(capture, getRandom(45000, 80000));
    if (!webcamRef.current) return;
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) return;
    const snaps = [...snapsRef.current, imageSrc];
    if (snaps.length > 10) snaps.splice(0, 1);
    snapsRef.current = snaps;
    setImgSnaps(snaps);
    // Re-evaluate task state: item.isComplete may have changed since the last render
    // without causing a prop reference change, leaving settingProps stale.
    setSettingProps(examineTask(liveRef.current.settings, getLiveProps()));
  }, [getLiveProps, webcamRef]);

  const beginCaptureTimer = () => {
    if (item.itemType !== ItemTypes.position || (item as TaskBasketItem & { hasMetronome?: boolean }).hasMetronome) return;
    if (!captureTimerHandle.current) captureTimerHandle.current = setTimeout(capture, getRandom(45000, 80000));
    capture();
  }

  useEffect(() => {
    return () => {
      if (captureTimerHandle.current) clearTimeout(captureTimerHandle.current);
      if (stateRefreshHandle.current) clearInterval(stateRefreshHandle.current);
      if (item.itemType === ItemTypes.position && !(item as TaskBasketItem & { hasMetronome?: boolean }).hasMetronome && downloadFilesOnCompleteRef.current) downloadFiles(snapsRef.current);
      if (completionAlertTimeoutRef.current) {
        clearTimeout(completionAlertTimeoutRef.current);
        completionAlertTimeoutRef.current = null;
      }
      if (completionAlertAudioRef.current) {
        completionAlertAudioRef.current.pause();
        completionAlertAudioRef.current.currentTime = 0;
        completionAlertAudioRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(COMPLETION_ALERT_MODE_KEY, completionAlertMode);
  }, [completionAlertMode]);

  const stopCompletionAlert = useCallback(() => {
    if (completionAlertTimeoutRef.current) {
      clearTimeout(completionAlertTimeoutRef.current);
      completionAlertTimeoutRef.current = null;
    }
    if (completionAlertAudioRef.current) {
      completionAlertAudioRef.current.pause();
      completionAlertAudioRef.current.currentTime = 0;
      completionAlertAudioRef.current = null;
    }
  }, []);

  const playCompletionAlert = useCallback(() => {
    stopCompletionAlert();
    const audio = new Audio(attendanceBellAudio);
    audio.loop = true;
    completionAlertAudioRef.current = audio;
    void audio.play().catch(() => {
      // Browser autoplay restrictions may block audio until user interaction.
    });

    if (completionAlertMode === 'continuous') return;

    const durationMs = completionAlertMode === '30s' ? 30000 : 5000;
    completionAlertTimeoutRef.current = setTimeout(() => {
      stopCompletionAlert();
    }, durationMs);
  }, [completionAlertMode, stopCompletionAlert]);

  const primeCompletionAlertAudio = useCallback(() => {
    const audio = new Audio(attendanceBellAudio);
    audio.muted = true;
    void audio.play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;
      })
      .catch(() => {
        // Best-effort only: priming may be blocked in some environments.
      });
  }, []);

  const downloadFiles = (files: string[]) => {
    files.forEach((f, idx) => {
      const link = document.createElement('a');
      link.href = f;
      link.download = `${item.name}_${idx}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const canUseSpeedUpDebug = import.meta.env.DEV
    && import.meta.env.MODE === 'staging'
    && (item.itemType === ItemTypes.task || item.itemType === ItemTypes.penalty)
    && settingProps.timed
    && !item.isComplete;

  const speedUpToOneMinute = () => {
    const now = new Date();
    const scheduledItems = liveRef.current.settings.getTaskSchedule();
    const currentItem = liveRef.current.props.item as BaseTimedBasketItem & (TaskBasketItem | PenaltyBasketItem);
    const scheduledItem = scheduledItems[currentItem.key] as BaseTimedBasketItem & (TaskBasketItem | PenaltyBasketItem) | undefined;
    const taskItem = scheduledItem ?? currentItem;

    taskItem.isPaused = false;
    taskItem.isComplete = false;
    taskItem.startedTime = now;
    taskItem.timerTicks = 1;
    taskItem.endTime = new Date(now.getTime() + 60000);

    liveRef.current.settings.updateTaskState(taskItem as any);

    // Keep the dialog's current item reference aligned to avoid stale-time flicker.
    currentItem.isPaused = taskItem.isPaused;
    currentItem.isComplete = taskItem.isComplete;
    currentItem.startedTime = taskItem.startedTime;
    currentItem.timerTicks = taskItem.timerTicks;
    currentItem.endTime = taskItem.endTime;

    setSettingProps(examineTask(liveRef.current.settings, liveRef.current.props));
  };

  const unknownEventDue = (settingProps.triggerEventDef && !settingProps.awaitingEventTask);
  const displayTitle = (item.name || item.description || '').trim();
  const displayDescription = (item.description || '').trim();
  const collapsedDescriptionPreview = displayDescription.length > 160
    ? `${displayDescription.slice(0, 157).trimEnd()}...`
    : displayDescription;
  const isCountdownInProgress = !!settingProps.timed && settingProps.inProgress && !settingProps.showCompletedIcon;
  const showCountdownFocus = isCountdownInProgress && settingProps.awaitTaskComplete;
  const wasCountdownFocusRef = useRef(showCountdownFocus);
  const [descriptionExpanded, setDescriptionExpanded] = useState<boolean>(!isCountdownInProgress);

  useEffect(() => {
    completionAlertPlayedRef.current = false;
    completionRunStartedRef.current = false;
    stopCompletionAlert();
  }, [item.key, stopCompletionAlert]);

  useEffect(() => {
    if (!settingProps.awaitTaskComplete) return;
    if (settingProps.inProgress) {
      completionRunStartedRef.current = true;
      completionAlertPlayedRef.current = false;
      return;
    }

    if (!completionRunStartedRef.current) return;

    const completionReached = item.isComplete
      || settingProps.showCompletedIcon
      || settingProps.percentRemaining <= 0;

    if (!completionReached) return;
    if (completionAlertPlayedRef.current) return;

    completionAlertPlayedRef.current = true;
    playCompletionAlert();
  }, [
    item.isComplete,
    playCompletionAlert,
    settingProps.awaitTaskComplete,
    settingProps.inProgress,
    settingProps.percentRemaining,
    settingProps.showCompletedIcon,
  ]);

  useEffect(() => {
    const wasCountdownFocus = wasCountdownFocusRef.current;
    wasCountdownFocusRef.current = showCountdownFocus;

    if (!settingProps.awaitTaskComplete) return;
    if (!wasCountdownFocus || showCountdownFocus) return;
    if (!completionRunStartedRef.current || completionAlertPlayedRef.current) return;

    const completionReached = item.isComplete
      || settingProps.showCompletedIcon
      || settingProps.percentRemaining <= 0;
    if (!completionReached) return;

    completionAlertPlayedRef.current = true;
    playCompletionAlert();
  }, [
    item.isComplete,
    playCompletionAlert,
    settingProps.awaitTaskComplete,
    settingProps.percentRemaining,
    settingProps.showCompletedIcon,
    showCountdownFocus,
  ]);

  useEffect(() => {
    if (isCountdownInProgress) {
      setDescriptionExpanded(false);
      return;
    }
    setDescriptionExpanded(true);
  }, [isCountdownInProgress, item.key]);
  const linkedChildrenSection = linkedChildTasks.length > 0 && (
    <div style={{ marginTop: 16 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Linked Tasks</Typography>
      <DialogContentText sx={{ mb: 1 }}>
        These tasks will start automatically with this event, and live progress is shown below.
      </DialogContentText>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 260, overflowY: 'auto', pr: 0.5 }}>
        {linkedChildTasks.map((linkedItem, idx) => (
          <ScheduleItemCard
            key={`${linkedItem.key}-${idx}`}
            item={linkedItem}
            play={() => ({})}
            showRemainingTime={true}
            defaultImage={props.defaultImage}
            checkEventComplete={checkEventComplete}
            isOwned={props.isOwned}
            isOwnerView={props.isOwnerView}
            readonly
            compactMode
            hidePrintButton
            showScheduleDialog={showScheduleDialogNoop}
          />
        ))}
      </Box>
    </div>
  );

  if (showCountdownFocus) {
    return (
      <Dialog
        open={true}
        fullScreen={false}
        PaperProps={{ style: { padding: 10, overflowX: 'hidden', width: 'min(96vw, 980px)', maxWidth: '96vw' } }}
        maxWidth={'lg'}
        onClose={handleCloseClick}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', overflowY: 'hidden', paddingLeft: 8, paddingRight: 8 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: '62vh', py: 1 }}>
            <ActivityRings
              theme={'light'}
              legend={false}
              data={[
                {
                  value: unknownEventDue ? 0.01 : ((100 - settingProps.percentRemaining) || 1) / 100,
                  color: green[200],
                  backgroundColor: red[200]
                },
              ]}
              config={{
                width: 560,
                height: 420,
                radius: 130,
                ringSize: 40,
              }}
            />
          </Box>
        </DialogContent>
        {(settingProps.showReportFailedButton || canUseSpeedUpDebug) && <DialogActions>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 'auto', flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">Completion Alert:</Typography>
            <Button size="small" variant={completionAlertMode === 'default' ? 'contained' : 'outlined'} onClick={() => setCompletionAlertMode('default')}>
              Default (5s)
            </Button>
            <Button size="small" variant={completionAlertMode === '30s' ? 'contained' : 'outlined'} onClick={() => setCompletionAlertMode('30s')}>
              30s
            </Button>
            <Button size="small" variant={completionAlertMode === 'continuous' ? 'contained' : 'outlined'} onClick={() => setCompletionAlertMode('continuous')}>
              Continuous
            </Button>
          </Box>
          {settingProps.showReportFailedButton && <Button variant="outlined" color="error" onClick={() => {
            settingProps.failed();
          }}>
            Failed {<Report />}
          </Button>}
          {canUseSpeedUpDebug && <Button variant="outlined" color="warning" onClick={() => {
            speedUpToOneMinute();
          }}>
            Speed to 1 Minute
          </Button>}
        </DialogActions>}
      </Dialog>
    );
  }

  return (
    <Dialog
      open={true}
      fullScreen={false}
      PaperProps={{ style: { padding: 10 } }}
      maxWidth={'lg'}
      onClose={handleCloseClick}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={{ display: 'flex', alignItems: 'center', fontFamily: 'Trebuchet MS', py: 1 }}>
        <IconButton
          size="small"
          onClick={() => settings.toggleFavourite(item)}
          title='Mark as Favourite'
          sx={{ mr: 1 }}
        >
          {settings?.getFavourites().includes(item.key) ? <Favorite sx={{ color: red[900] }} /> : <FavoriteBorderOutlined />}
        </IconButton>
        <span style={{ flex: 1, textAlign: 'center' }}>{displayTitle}</span>
        {settingProps.canCloseWindow
          ? <IconButton size="small" onClick={handleClose} title='Close' sx={{ ml: 1 }}>
            <Close sx={{ color: red[900] }} />
          </IconButton>
          : <span style={{ width: 34, flexShrink: 0 }} />}
      </DialogTitle>
      <Breakpoint large up>
        <DialogContent
          style={{ width: 800, overflow: 'hidden' }}>

          <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
            <ImageDisplayField
              item={item}
              allowFix
              allowUpdate={!readonly}
              size='Larger'
              updated={() => { }}
            />
            {(settingProps.timed)
              && <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}>
                <Typography variant="subtitle2" gutterBottom>Task Info</Typography>
                {(item as TaskBasketItem).endTriggerEventName && <DialogContentText style={{ width: 200 }}>
                  {`This Task runs until ${(item as TaskBasketItem).endTriggerEventName} next is due`}
                </DialogContentText>}
                {!unknownEventDue && <Typography variant="h6">Progress: {`${Math.round(100 - settingProps.percentRemaining)}%`}</Typography>}
                {!unknownEventDue && <div style={{ width: 200 }}>
                  <ActivityRings
                    theme={'light'}
                    legend={false}
                    data={[
                      {
                        value: ((100 - settingProps.percentRemaining) || 1) / 100,
                        color: green[200],
                        backgroundColor: red[200]
                      },
                    ]}
                    config={{
                      width: 200,
                      height: 100,
                      radius: 32,
                      ringSize: 14,
                    }}
                  />
                </div>}
                {!item.isComplete && settingProps.showRemainingMinutes && !unknownEventDue && <span className="basketItemTime">
                  {(item as BaseTimedBasketItem).remainingMinutes} Remaining
                </span>}
                {item.isComplete && <span className="basketItemTime">Complete
                </span>}
                {settingProps.showAwaitingRequestedByMessage && <DialogContentText style={{ width: 240 }}>
                  {settingProps.awaitingRequestedByMessage}
                </DialogContentText>}
              </div>}
            {item.itemType === ItemTypes.position && !(item as TaskBasketItem & { hasMetronome?: boolean }).hasMetronome && <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
              <span>Owner Cam: <HelpIcon text={'This is a fun feature that makes you think you are being watched. This data is not presently sent anywhere and if it ever was passed to your owner, you would be asked and have to give permission first. Revoke WebCam permissions to stop capturing images.'} /></span>
              <Webcam height={200} width={200} ref={webcamRef} mirrored />
            </div>}

          </div>
          {item.itemType === ItemTypes.position && !(item as TaskBasketItem & { hasMetronome?: boolean }).hasMetronome && <div>
            <DialogContentText>Image captures for Position<br /><b className="red">(Note: these are not shared or saved - fun only)</b></DialogContentText>
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', marginTop: 18, marginLeft: 50 }}>{imgSnaps.map((i, idx) => <span style={{ margin: 2 }} key={idx}>
              <a href={i}
                onClick={(args) => {
                  args.stopPropagation();
                }}
                rel="noopener noreferrer"
                target="_blank">
                <img src={i} height={75} />
              </a></span>)}</div>
          </div>}
          <div>
            <p style={{ textAlign: 'center' }}><DialogContentText><b>{item.getBasketText(checkEventComplete)}</b></DialogContentText></p>
            <Typography variant="caption" display="block" color="text.secondary">{item.scheduleReason}</Typography>
            {displayDescription && (
              <>
                {isCountdownInProgress && (
                  <Button
                    size="small"
                    variant="text"
                    sx={{ mt: 0.5, px: 0 }}
                    onClick={() => setDescriptionExpanded(prev => !prev)}
                  >
                    {descriptionExpanded ? 'Hide Description' : 'Show Description'}
                  </Button>
                )}
                {isCountdownInProgress && !descriptionExpanded && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {collapsedDescriptionPreview}
                  </Typography>
                )}
                <Collapse in={descriptionExpanded} timeout="auto" unmountOnExit>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {displayDescription}
                  </Typography>
                </Collapse>
              </>
            )}
            {linkedChildrenSection}
          </div>

          {/* {isUniqueTask && settingProps.timed && <DialogContentText>This is a timed task, click start below to begin the task when prepared and following the instructions. A timer will count down until you can stop following as directed.</DialogContentText>}
        {!isUniqueTask && settingProps.timed && <DialogContentText>This is a timed task, click start below to begin the task when prepared and following the instructions. The task then appears as "In Progress" and will then count down until you can stop following as directed.</DialogContentText>} */}

        </DialogContent>
      </Breakpoint>
      <Breakpoint medium down>
        <DialogContent
          style={{ width: 'auto', overflow: 'hidden' }}>

          <div style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', textAlign: 'center' }}>
            <ImageDisplayField
              item={item}
              allowFix
              allowUpdate={!readonly}
              size='Default'
              updated={() => { }}
            />
            {(settingProps.timed)
              && <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}>
                <Typography variant="subtitle2" gutterBottom>Task Info</Typography>
                {(item as TaskBasketItem).endTriggerEventName && <DialogContentText style={{ width: 200 }}>
                  {`This Task runs until ${(item as TaskBasketItem).endTriggerEventName} next is due`}
                </DialogContentText>}
                {!unknownEventDue && <Typography variant="h6">Progress: {`${Math.round(100 - settingProps.percentRemaining)}%`}</Typography>}
                {!unknownEventDue && <div style={{ width: 200 }}>
                  <ActivityRings
                    theme={'light'}
                    legend={false}
                    data={[
                      {
                        value: ((100 - settingProps.percentRemaining) || 1) / 100,
                        color: green[200],
                        backgroundColor: red[200]
                      },
                    ]}
                    config={{
                      width: 200,
                      height: 100,
                      radius: 32,
                      ringSize: 14,
                    }}
                  />
                </div>}
                {!item.isComplete && settingProps.showRemainingMinutes && !unknownEventDue && <span className="basketItemTime">
                  {(item as BaseTimedBasketItem).remainingMinutes} Remaining
                </span>}
                {item.isComplete && <span className="basketItemTime">Complete
                </span>}
                {settingProps.showAwaitingRequestedByMessage && <DialogContentText style={{ width: 240 }}>
                  {settingProps.awaitingRequestedByMessage}
                </DialogContentText>}
              </div>}
          </div>
          <div>
            <p><DialogContentText><b>{item.getBasketText(checkEventComplete)}</b></DialogContentText></p>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>{item.scheduleReason}</Typography>
            {displayDescription && (
              <>
                {isCountdownInProgress && (
                  <Button
                    size="small"
                    variant="text"
                    sx={{ mt: 0.25, px: 0 }}
                    onClick={() => setDescriptionExpanded(prev => !prev)}
                  >
                    {descriptionExpanded ? 'Hide Description' : 'Show Description'}
                  </Button>
                )}
                {isCountdownInProgress && !descriptionExpanded && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {collapsedDescriptionPreview}
                  </Typography>
                )}
                <Collapse in={descriptionExpanded} timeout="auto" unmountOnExit>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {displayDescription}
                  </Typography>
                </Collapse>
              </>
            )}
            {linkedChildrenSection}
          </div>

          {/* {isUniqueTask && settingProps.timed && <DialogContentText>This is a timed task, click start below to begin the task when prepared and following the instructions. A timer will count down until you can stop following as directed.</DialogContentText>}
        {!isUniqueTask && settingProps.timed && <DialogContentText>This is a timed task, click start below to begin the task when prepared and following the instructions. The task then appears as "In Progress" and will then count down until you can stop following as directed.</DialogContentText>} */}

        </DialogContent>
      </Breakpoint>
      <DialogActions>
        {item.itemType === ItemTypes.position && !(item as TaskBasketItem & { hasMetronome?: boolean }).hasMetronome
          && <FormControlLabel
            control={
              <Checkbox
                checked={downloadFilesOnComplete}
                onChange={(_, checked) => {
                  setDownloadFilesOnComplete(checked);
                  downloadFilesOnCompleteRef.current = checked;
                }}
                color="primary"
              />
            }
            label="Download files on complete"
          />}
        {settingProps.showStartButton && <Button className='flashingTab' onClick={() => {
          primeCompletionAlertAudio();
          settingProps.start();
          // callback(); show the time remaining and the progress chart in this case
        }} color="primary">
          Start {<PlayArrow style={{ color: green[900] }} />}
        </Button>}
        {settingProps.showBeginTimerButton && <Button className='flashingTab' onClick={() => {
          primeCompletionAlertAudio();
          settingProps.start();
          beginCaptureTimer();
          // we will not let them shut the window until it is completed
        }} color="primary">
          Begin Timer {<PlayArrow style={{ color: green[900] }} />}
        </Button>}
        {settingProps.showSpin && <Button className='flashingTab' onClick={() => {
          settingProps.spin();
          setTimeout(() => {
            setSettingProps(examineTask(liveRef.current.settings, liveRef.current.props));
          }, 0);
          props.onSpin?.();
        }} color="primary">
          Spin {<Casino style={{ color: green[900] }} />}
        </Button>}
        {canUseSpeedUpDebug && <Button variant="outlined" color="warning" onClick={() => {
          speedUpToOneMinute();
        }}>
          Speed to 1 Minute
        </Button>}
        {settingProps.showWebLinkButton && <Button variant="outlined" onClick={() => {
          settingProps.openInBrowser();
        }} color="primary">
          Open {<OpenInBrowser />}
        </Button>}
        {settingProps.hasHygiene && <Button variant="outlined" onClick={() => {
          settingProps.hygiene();
          closeAfterPersist();
          //TODO: maybe this one belongs on the main screen? bit hidden to go in to the dialog to see it
        }} color="primary">
          Begin Hygiene {<Wash />}
        </Button>}
        {settingProps.showDoneButton && <Button className='flashingTab' onClick={() => {
          settingProps.done();
          closeAfterPersist();
        }} color="primary">
          Completed Task {<Check style={{ color: green[900] }} />}
        </Button>}
        {settingProps.showViewVoteButton && <Button variant="outlined" onClick={() => {
          settingProps.viewVote();
          //TODO: maybe this one belongs on the main screen? bit hidden to go in to the dialog to see it
          handleClose();
        }} color="primary">
          View Vote {<HowToVote />}
        </Button>}
        {settingProps.showDecrementCountButton && <Button className='flashingTab' onClick={() => {
          settingProps.done();
          closeAfterPersist();
        }} color="primary">
          Completed {(props.item as TaskBasketItem).expectedCount} {CountType[(item as TaskBasketItem).countType]}{<Check style={{ color: green[900] }} />}
        </Button>}
        {settingProps.showReportFailedButton && <Button variant="outlined" color="error" onClick={() => {
          settingProps.failed();
        }}>
          Failed {<Report />}
        </Button>}
        {settingProps.showItemRequestIcon && <Button variant="outlined" onClick={() => {
          settingProps.requestInfo();
        }} color="primary">
          Make Request {<QuestionAnswer />}
        </Button>}

        {item.itemType === ItemTypes.event && <Button variant="outlined" onClick={() => {
          printScheduleEvent(item, item.getBasketText(checkEventComplete));
        }} color="primary">
          Print {<Print />}
        </Button>}

        {settingProps.showContinueButton && <Button className='flashingTab' onClick={() => {
          handleClose();
        }} color="primary">
          Close and Continue {<ArrowRight />}
        </Button>}
      </DialogActions>
    </Dialog>
  );
}